<template>
  <div>
    <div class="p-booking__filters">
      <div class="p-booking__title">{{ dictionary.movieSelection }}</div>
    </div>
    <div class="p-booking__cinemas">
      <Alert
        v-if="showCinemaOfflineAlert"
        type="warning"
        :messages="[dictionary.cinemaChoiceError]"></Alert>

      <div v-if="cinemas.length">
        <template v-if="desktop">
          <div class="form-chunk-list">
            <div v-for="city in cities" :key="city" class="form-chunk">
              <input
                :id="city"
                v-model="selectedCity"
                class="form-check-input"
                type="radio"
                name="city"
                :value="city"
                :disabled="isDisabledCity(city)"
                @change="chooseCity" />
              <label class="form-check-label" :for="city">
                {{ city }}
              </label>
            </div>
          </div>
          <div v-if="selectedCity" class="form-chunk-list">
            <div
              v-for="item in cinemasOfSelectedCity"
              :key="item.value"
              class="form-chunk">
              <input
                :id="item.value"
                v-model="selected"
                class="form-check-input"
                type="radio"
                name="cinema"
                :value="item.value"
                :disabled="isDisabled(item)"
                @change="chooseCinema" />
              <label class="form-check-label" :for="item.value">
                {{ item.display }}
              </label>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="s-playing-now__options__filters__element form-floating">
            <select
              id="chooseCinema"
              v-model="selected"
              class="form-select"
              :class="{ 'form-select--mobile': !desktop }"
              @change="chooseCinema">
              <option value="" disabled>
                {{ dictionary.chooseMovieTheater }}
              </option>
              <optgroup v-for="city in cities" :key="city" :label="city">
                <option
                  v-for="item in cinemas.filter((i) => i.city === city)"
                  :key="item.value"
                  :value="item.value"
                  :disabled="isDisabled(item)">
                  {{ item.display }}
                </option>
              </optgroup>
            </select>
            <label for="chooseCinema" class="form-label">
              {{ dictionary.movieTheater }}
            </label>
          </div>
        </template>
      </div>
      <NoResults v-else />
    </div>
  </div>
</template>

<script setup>
import jquery from 'jquery'
import { computed, nextTick, onMounted, ref } from 'vue'
import useViewport from '../../../../features/viewport'
import Alert from '../../Shared/Alert.vue'
import { useBookingStore } from '../../../store/BookingStore'
import NoResults from './../../../components/Shared/NoResults.vue'
import * as lib from './../../../../lib/lib'
import _ from 'underscore'

const offsetY = lib.scrDesktops() ? 50 : 95
const store = useBookingStore()
const { selectedCinema } = store.bookingProgress
// const selectedMovieCinemas = store.bookingProgress.selectedMovie.cinemas
const selectedRoomType = store.getSelectedFilters.roomTypes
const selectedScreenType = store.getSelectedFilters.screenTypes
const cinemas = store.getAllCinemas
const { desktop } = useViewport()
const emit = defineEmits(['choose-cinema', 'choose-city', 'close-error'])
const selected = ref(selectedCinema.value ? selectedCinema.value : '')
const allScreens = lib.getData('bookingData').screens

// cities
const selectedCity = ref('')
const cities = computed(() => {
  if (!cinemas || !cinemas.length) {
    return []
  }

  return [...new Set(cinemas.map((item) => item.city))].sort()
})
const cinemasOfSelectedCity = computed(() => {
  return cinemas.filter((item) => item.city === selectedCity.value)
})

defineProps({
  showCinemaOfflineAlert: {
    default: false,
    type: Boolean,
  },
})

const isDisabled = (item) => {
  const selectedMovie = store.bookingProgress.selectedMovie

  const filteredRecords = allScreens.filter((i) => {
    const screenTypeMatch =
      selectedScreenType === 'All' ||
      selectedScreenType === undefined ||
      i.screenType === selectedScreenType

    const roomTypeMatch =
      selectedRoomType === 'All' ||
      selectedScreenType === undefined ||
      i.roomTypes.includes(selectedRoomType)

    const movieMatch = i.scheduledFilmId === selectedMovie.id

    return screenTypeMatch && roomTypeMatch && movieMatch
  })

  const newArray = []

  filteredRecords.forEach((item) => {
    if (!newArray.includes(item.cinemaId)) {
      newArray.push(item.cinemaId)
    }
  })

  return !newArray.includes(item.value)
}

const isDisabledCity = (city) => {
  const cinemasOfCity = cinemas.filter((item) => item.city === city)
  return cinemasOfCity.every((item) => isDisabled(item))
}

const chooseCity = () => {
  emit('choose-city')

  if (
    cinemasOfSelectedCity.value.length === 1 &&
    !isDisabled(cinemasOfSelectedCity.value[0])
  ) {
    selected.value = cinemasOfSelectedCity.value[0].value
    chooseCinema()
    return
  }

  const enabledCinemas = getEnabledCinemas(cinemasOfSelectedCity.value)
  if (enabledCinemas.length === 1) {
    selected.value = enabledCinemas[0].value
    chooseCinema()
  }
}

const chooseCinema = async () => {
  const entry = cinemas.find((item) => item.value === selected.value)
  store.selectCinema(entry)
  emit('choose-cinema')

  await nextTick()
  lib.scrollToElem(jquery('#dateStep'), offsetY)
}

const getEnabledCinemas = (cinemas) => {
  return cinemas.filter((item) => !isDisabled(item))
}

onMounted(() => {
  const preselectedCinemaValue = store.getSelectedFilters.cinemas

  if (!preselectedCinemaValue || preselectedCinemaValue === 'All') {
    return
  }

  const preselectedCinema = cinemas.find(
    (item) => item.value === preselectedCinemaValue,
  )

  // Skip preselection when the preferred cinema has no sessions for the selected
  // movie — it is disabled in the UI, so a user could not have chosen it manually.
  if (!preselectedCinema || isDisabled(preselectedCinema)) {
    return
  }

  selectedCity.value = preselectedCinema.city
  selected.value = preselectedCinemaValue
  chooseCinema()
})
</script>
